import { RefObject, useCallback, useEffect, useState } from 'react';

import { Awaitable, Nullable } from '@/utils/globalTypes';

import useElementVisibility from './useElementVisibility';
import useScroll from './useScroll';

type UseInfiniteScrollOptions<
  TInfiniteScrollElement extends HTMLElement = HTMLElement,
> = {
  /**
   * The direction in which to listen the scroll.
   *
   * @default 'bottom'
   */
  direction?: 'top' | 'bottom' | 'left' | 'right';
  /**
   * The interval time between two load more (to avoid too many invokes).
   *
   * @default 100
   */
  interval?: number;
  /**
   * A function that determines whether more content can be loaded for a specific element.
   * Should return `true` if loading more content is allowed for the given element,
   * and `false` otherwise.
   */
  canLoad?: (el: TInfiniteScrollElement) => boolean;
  /**
   * The minimum distance between the bottom of the element and the bottom of the viewport
   *
   * @default 0
   */
  distance?: number;
};

type UseInfiniteScrollParams = {
  element: RefObject<Nullable<HTMLElement>>;
  onLoad: (state: ReturnType<typeof useScroll>) => Awaitable<void>;
  options: UseInfiniteScrollOptions;
};

function useInfiniteScroll({
  element,
  onLoad,
  options = {},
}: UseInfiniteScrollParams) {
  const {
    direction = 'bottom',
    interval = 100,
    distance = 0,
    canLoad = () => true,
  } = options;

  const [promise, setPromise] = useState<Promise<[void, unknown]> | null>(null);

  const scrollState = useScroll({
    element,
    offset: {
      [direction]: distance,
    },
  });
  const isElementVisible = useElementVisibility({ element: element.current });

  const isLoading = !!promise;
  const hasArrived = scrollState.arrivedState[direction];

  const checkAndLoad = useCallback(() => {
    scrollState.measure();

    if (!element.current || !isElementVisible || !canLoad(element.current))
      return;

    const { scrollHeight, clientHeight, scrollWidth, clientWidth } =
      element.current;
    const isNarrower =
      direction === 'bottom' || direction === 'top'
        ? scrollHeight <= clientHeight
        : scrollWidth <= clientWidth;

    if (scrollState.arrivedState[direction] || isNarrower) {
      const _promise = Promise.all([
        onLoad(scrollState),
        new Promise((resolve) => setTimeout(resolve, interval)),
      ]).finally(() => {
        setPromise(null);
      });

      setPromise(_promise);
    }
  }, [
    element,
    isElementVisible,
    direction,
    scrollState,
    interval,
    onLoad,
    canLoad,
  ]);

  useEffect(() => {
    if (isLoading) return;

    checkAndLoad();
  }, [checkAndLoad, hasArrived, isElementVisible, isLoading]);

  return {
    isLoading,
  };
}

export default useInfiniteScroll;
